import React, { useEffect, useState } from "react";
import { PixelStyle } from "../../components/layout";
import TableLayout, { Td, Tr } from "../../components/tableLayout";
import { BsCopy } from "react-icons/bs";
import { FiEdit3 } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { NavLink } from "react-router-dom";
import { GoDownload } from "react-icons/go";
import ExportToExcel from "../../components/exportToExcel";
import { ToastContainer } from "react-toastify";
import { graphqlRequest } from "../../services/apiService";
import { ConfirmationModal, AlertModal } from "../../components/Modal";
import { usePagination } from "../../components/Pagination";
import Pagination from "../../components/Pagination";

const ViewReport = () => {
  const [reportTemplates, setReportTemplates] = useState([]);
  const [filters, setFilters] = useState({
    reportName: "",
    company: "",
    location: "",
    status: "",
  });
  const [sortedReports, setSortedReports] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null); // "asc" or "desc"

  // Pagination
  const {
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems,
    paginatedData,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(sortedReports, 50);

  const TableHead = [
    "Report Name",
    "Company Name",
    "Location",
    "Status",
    "Action",
  ];

  const fetchAllTemplates = async (token) => {
    try {
      const query = `
        query GetAllTemplates {
          getAllTemplates {
            id
            status
            reportName
            companyId
            createdAt
            updatedAt
            isEditable
            company {
              id
              company
              city
              zipCode
              address
              state
              industry
              clientName
              clientType
              streetAddress
            }
          }
        }
      `;

      const data = await graphqlRequest(query, {});
      return data.getAllTemplates;
    } catch (error) {
      console.error("Fetch error:", error);
      throw new Error(error.message || "Network error");
    }
  };

  // const { data, isLoading, isError, error } = useQuery({
  //   queryKey: ["fetchAllTemplates"],
  //   queryFn: () => fetchAllTemplates(), // Pass token here
  //   // staleTime: 5 * 60 * 1000,
  //   retry: 2,
  // });

  useEffect(() => {
    fetchAllTemplates().then((data) => setReportTemplates(data));
  }, []);

  // console.log("reportTemplates", reportTemplates, "=>", data);

  useEffect(() => {
    const filteredReports =
      reportTemplates?.filter((item) => {
        return (
          (filters.reportName === "" ||
            (item.reportName &&
              item.reportName
                .toLowerCase()
                .includes(filters.reportName.toLowerCase()))) &&
          (filters.company === "" ||
            (item.company?.company &&
              item.company.company
                .toLowerCase()
                .includes(filters.company.toLowerCase()))) &&
          (filters.location === "" ||
            (item.company?.address &&
              item.company.address
                .toLowerCase()
                .includes(filters.location.toLowerCase()))) &&
          (filters.status === "" ||
            (item.status &&
              item.status
                .toLowerCase()
                .startsWith(filters.status.toLowerCase())))
        );
      }) || [];

    if (sortField) {
      const sorted = [...filteredReports].sort((a, b) => {
        // Handle nested properties
        const getValue = (obj, path) => {
          return (
            path.split(".").reduce((acc, part) => acc && acc[part], obj) || ""
          );
        };

        const aValue = String(getValue(a, sortField)).toLowerCase();
        const bValue = String(getValue(b, sortField)).toLowerCase();

        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
      setSortedReports(sorted);
    } else {
      setSortedReports(filteredReports);
    }
  }, [filters, reportTemplates, sortField, sortOrder]);

  const headerFieldMapping = {
    "Report Name": "reportName",
    "Company Name": "company.company", // Nested company object
    Location: "company.address", // Nested company object
    Status: "status",
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else if (sortOrder === "desc") {
        setSortOrder(null);
        setSortField(null);
      } else {
        setSortOrder("asc");
      }
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const toggleSortOrder = (header) => {
    const field = headerFieldMapping[header];
    // console.log("header", field);
    if (field) {
      handleSort(field);
    }
  };

  // const handleDelete = (templateId) => {
  //   const isConfirmed = window.confirm(
  //     "Are you sure you want to delete this report template?"
  //   );
  //   if (!isConfirmed) return;

  //   const templateRef = ref(database, `reportTemplates/${templateId}`);
  //   remove(templateRef)
  //     .then(() => {
  //       setReportTemplates(
  //         reportTemplates.filter((template) => template.id !== templateId)
  //       );
  //     })
  //     .catch((error) => {
  //       setErrorMessage(`Error deleting report template: ${error.message}`);
  //     });
  // };

  const handleDelete = async (templateId) => {
    const template = reportTemplates.find(t => t.id === templateId);
    const templateName = template?.reportName || 'this report template';

    showConfirmModal({
      title: 'Delete Report Template',
      message: `Are you sure you want to delete "${templateName}"? This action cannot be undone.`,
      confirmVariant: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const query = `
            mutation DeleteTemplate($deleteTemplateId: ID!) {
              deleteTemplate(id: $deleteTemplateId) {
                success
                status
                message
                data
              }
            }
          `;

          const data = await graphqlRequest(query, { deleteTemplateId: templateId });

          if (data?.deleteTemplate?.success) {
            // Update state to remove deleted template
            setReportTemplates((prevTemplates) =>
              prevTemplates.filter((template) => template.id !== templateId)
            );
            
            showAlertModal({
              title: 'Success',
              message: `"${templateName}" has been deleted successfully.`,
              type: 'success'
            });
            
            return data.deleteTemplate;
          } else {
            throw new Error(data?.deleteTemplate?.message || "Deletion failed");
          }
        } catch (error) {
          console.error("Delete error:", error);
          showAlertModal({
            title: 'Error',
            message: `Error deleting report template: ${error.message}`,
            type: 'error'
          });
          return null;
        }
      },
      onCancel: () => {
        // User cancelled, do nothing
      }
    });
  };

  const handleReplicate = async (templateId) => {
    try {
      const query = `
        mutation DuplicateTemplate($duplicateTemplateId: ID!) {
          duplicateTemplate(id: $duplicateTemplateId) {
            success
            status
            message
            data
          }
        }
      `;

      const data = await graphqlRequest(query, { duplicateTemplateId: templateId });

      if (data?.duplicateTemplate?.success) {
        const duplicatedName = data?.duplicateTemplate?.data?.reportName || 'Report';
        
        showAlertModal({
          title: 'Success',
          message: `"${duplicatedName}" has been duplicated successfully.`,
          type: 'success'
        });

        // Refresh the templates list
        fetchAllTemplates().then((data) => setReportTemplates(data));
        return data.duplicateTemplate;
      } else {
        throw new Error(data?.duplicateTemplate?.message || "Failed to duplicate template");
      }
    } catch (error) {
      console.error("Error duplicating template:", error);
      showAlertModal({
        title: 'Error',
        message: `Error copying record: ${error.message}`,
        type: 'error'
      });
      return null;
    }
  };

  const ExportPayload = (data) => {
    const payload = Array.isArray(data) ? data : [data];

    // Define fields to exclude
    const excludeFields = [
      "id",
      "createdAt",
      "updatedAt",
      "isEditable",
      "clientType",
      "companyLocationId",
      "companyId",
    ];

    // Updated header mapping for desired fields only
    const headerMapping = {
      status: "Status",
      reportName: "Report Name",
      "company.company": "Company Name",
      "company.city": "City",
      "company.state": "State",
      "company.zipCode": "Zip Code",
      "company.address": "Address",
      "company.industry": "Industry",
      "company.clientName": "Client Name",
    };

    const cleanedLeads = payload.map((item) => {
      const renamedObject = {};

      // Flatten and map only the desired fields
      Object.keys(item).forEach((key) => {
        if (!excludeFields.includes(key)) {
          if (key === "company" && item[key]) {
            // Handle nested company object, excluding clientType
            Object.keys(item[key]).forEach((companyKey) => {
              if (!excludeFields.includes(companyKey)) {
                const mappedKey =
                  headerMapping[`company.${companyKey}`] || companyKey;
                renamedObject[mappedKey] = item[key][companyKey];
              }
            });
          } else {
            // Handle top-level fields
            const newKey = headerMapping[key] || key;
            renamedObject[newKey] = item[key];
          }
        }
      });

      return renamedObject;
    });

    return cleanedLeads;
  };


  // Modal states
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    confirmVariant: 'danger'
  });

  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [alertModalData, setAlertModalData] = useState({
    title: '',
    message: '',
    type: 'info'
  });

  // Helper functions for modals
  const showConfirmModal = (data) => {
    setConfirmModalData(data);
    setIsConfirmModalOpen(true);
  };

  const showAlertModal = (data) => {
    setAlertModalData(data);
    setIsAlertModalOpen(true);
  };

  const handleConfirmModalConfirm = () => {
    if (confirmModalData.onConfirm) {
      confirmModalData.onConfirm();
    }
    setIsConfirmModalOpen(false);
  };

  const handleConfirmModalClose = () => {
    setIsConfirmModalOpen(false);
  };

  const handleAlertModalClose = () => {
    setIsAlertModalOpen(false);
  };


  const handleUpdateReportTemplate = async (newStatus, id) => {
    try {
      const query = `
        mutation StatusUpdateTemplate($statusUpdateTemplateId: ID!, $status: String!) {
          statusUpdateTemplate(id: $statusUpdateTemplateId, status: $status) {
            success
            message
            data
          }
        }
      `;

      const data = await graphqlRequest(query, {
        statusUpdateTemplateId: id,
        status: newStatus,
      });

      if (data?.statusUpdateTemplate?.success) {
        // Update local state if needed
        setReportTemplates((prevTemplates) =>
          prevTemplates.map((template) =>
            template.id === id ? { ...template, status: newStatus } : template
          )
        );
        
        const template = reportTemplates.find(t => t.id === id);
        const templateName = template?.reportName || 'Report';
        
        showAlertModal({
          title: 'Success',
          message: `"${templateName}" status has been updated to ${newStatus}.`,
          type: 'success'
        });
        
        return data.statusUpdateTemplate;
      } else {
        throw new Error(
          data?.statusUpdateTemplate?.message || "Status update failed"
        );
      }
    } catch (error) {
      console.error("Error updating report template status:", error);
      showAlertModal({
        title: 'Error',
        message: `Error updating report template: ${error.message}`,
        type: 'error'
      });
      return null;
    }
  };

  // console.log("filters", filters);

  const [status, setStatus] = useState({});

  return (
    <>
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={handleConfirmModalClose}
        onConfirm={handleConfirmModalConfirm}
        title={confirmModalData.title}
        message={confirmModalData.message}
        confirmText={confirmModalData.confirmText}
        cancelText={confirmModalData.cancelText}
        confirmVariant={confirmModalData.confirmVariant}
      />
      
      {/* Alert Modal */}
      <AlertModal
        isOpen={isAlertModalOpen}
        onClose={handleAlertModalClose}
        title={alertModalData.title}
        message={alertModalData.message}
        type={alertModalData.type}
      />
      <ToastContainer />
      <div className={`w-full text-sm h-full ${PixelStyle} `}>
        <HeaderContainer pageTitle="View Report" isRedirecting={false}>
          <span className="text-black">View report</span>
        </HeaderContainer>
        <div className="flex justify-between items-center w-full px-5 py-1">
          <h4 className="text-sm font-semibold">Report Templates</h4>
          <button
            className="text-zinc-500 p-2 rounded flex items-center gap-2"
            onClick={() =>
              ExportToExcel(ExportPayload(reportTemplates), "View report")
            }
          >
            <GoDownload className="text-xl" />
          </button>
        </div>
        <div className=" bg-white rounded-2xl shadow-lg overflow-hidden">
          <TableLayout
            TableHead={TableHead}
            sortField={sortField}
            sortOrder={sortOrder}
            toggleSortOrder={toggleSortOrder}
            headerFieldMapping={headerFieldMapping}
            showPagination={false}
            maxHeight="75vh"
            enableTableScroll={true}
          >
            <>
              <tr className="border-b bg-zinc-50 text-xs ">
                {["reportName", "company", "location"].map((header, index) => (
                  <th scope="col" className="px-6 py-1" key={index}>
                    <input
                      placeholder={`Filter by ${
                        header === "name"
                          ? "Report Name"
                          : header === "company"
                          ? "Company Name"
                          : header === "location"
                          ? "Location"
                          : header
                      }`}
                      name={header}
                      value={filters[header]}
                      onChange={handleFilterChange}
                      className="border p-1 px-2 rounded-md font-normal outline-blue-500"
                    />
                  </th>
                ))}
                <th className="px-6 py-1">
                  <input
                    name="status"
                    onChange={handleFilterChange}
                    className="border p-1 px-2 rounded-md font-normal outline-blue-500"
                    placeholder="Filter by Status"
                    value={filters?.status}
                  />
                  {/* <select name="status" onChange={handleFilterChange}  className='border p-1 px-2 rounded-md font-normal outline-blue-500' >
                    <option value="">Filter by Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select> */}
                </th>
                <th></th>
              </tr>

              {paginatedData && paginatedData.length > 0 ? (
                paginatedData.map((item, index) => {
                  const isPermissionToEdit = item.isEditable;
                  // console.log("isPermissionToEdit", isPermissionToEdit);

                  // Use status from state or fallback to item status
                  const currentStatus = status[item?.id] || item?.status;
                  const location = item?.company;
                  return (
                    <Tr key={index}>
                      <Td>{item?.reportName}</Td>
                      <Td>{item?.company?.clientName}</Td>
                      <Td>{`${location?.address}, ${location?.city}, ${location?.state} ${location?.zipCode}`}</Td>
                      <Td>
                        <select
                          value={currentStatus}
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            setStatus((prev) => ({
                              ...prev,
                              [item.id]: newStatus,
                            }));
                            handleUpdateReportTemplate(newStatus, item?.id);
                          }}
                          className={` font-medium bg-zinc-50 py-1 px-2 rounded-md ${
                            currentStatus === "active"
                              ? "text-green-700"
                              : "text-red-700"
                          }`}
                        >
                          {currentStatus !== "active" &&
                            currentStatus !== "inactive" && (
                              <option>Select the status</option>
                            )}
                          <option
                            value="active"
                            className="text-green-700 text-sm"
                          >
                            Active
                          </option>
                          <option value="inactive" className="text-red-700 ">
                            Inactive
                          </option>
                        </select>
                      </Td>
                      <Td className="">
                        <div className="text-sm flex gap-1">
                          <button
                            className="hover:text-blue-500 hover:bg-blue-100 p-1.5 rounded-lg"
                            onClick={() => handleReplicate(item?.id)}
                          >
                            <BsCopy />
                          </button>
                          {/* <button className='hover:text-blue-500 hover:bg-blue-100 p-1.5 rounded-lg'>
                            <MdOutlineRemoveRedEye />
                          </button> */}

                          <NavLink
                            to={
                              isPermissionToEdit === true
                                ? `/viewreport/${item.id}`
                                : "/viewreport"
                            }
                          >
                            <button
                              className="hover:text-blue-500 hover:bg-blue-100 p-1.5 rounded-lg"
                              onClick={() => {
                                if (!isPermissionToEdit) {
                                  showAlertModal({
                                    title: 'Access Denied',
                                    message: 'Report has been assigned and cannot be edited.',
                                    type: 'warning'
                                  });
                                }
                              }}
                            >
                              <FiEdit3 />
                            </button>
                          </NavLink>

                          <button
                            className="hover:text-red-500 hover:bg-red-100 p-1.5 rounded-lg"
                            onClick={() => handleDelete(item?.id)}
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </div>
                      </Td>
                    </Tr>
                  );
                })
              ) : (
                <div className="flex justify-center items-center w-full h-1/2 ">
                  {/* <Loader /> */}
                </div>
              )}
            </>
          </TableLayout>
        </div>

        {/* Pagination - Outside shadow card */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onItemsPerPageChange={handleItemsPerPageChange}
          className="mt-4"
        />

        <div className="py-32"></div>
      </div>
    </>
  );
};

export default ViewReport;

export const PageTitle = ({ title, container }) => {
  return (
    <div className="flex justify-between">
      <div className="text-3xl font-semibold capitalize">{title}</div>
      <div className="">{container}</div>
    </div>
  );
};

export const Redirecting = ({ children }) => (
  <div className="font-semibold  text-zinc-500  flex gap-2">
    <NavLink to="/createreport">createreport</NavLink>
    <span>/</span>
    {/* <span>View report</span> */}
    {children}
  </div>
);

export const HeaderContainer = ({ children, pageTitle, container, isRedirecting = true }) => (
  <div className="flex flex-col gap-2 my-7 capitalize ">
    {isRedirecting && <Redirecting>{children}</Redirecting>}

    <PageTitle title={pageTitle} container={container} />
  </div>
);
